import { forwardRef, useEffect, useId, useRef, useState } from 'react';
import type { JSX, KeyboardEvent, ReactNode, TextareaHTMLAttributes } from 'react';
import { IconChevronDown, IconPlus } from '../icons/index.js';
import { normaliserHex } from '../lib/branding.js';
import { cn } from '../lib/cn.js';
import { Button } from './Button.js';
import { Input } from './Input.js';
import type { InputProps } from './Input.js';
import { OptionList, SEUIL_RECHERCHE } from './OptionList.js';
import type { OptionListEntry } from './OptionList.js';

/**
 * LES CONTROLES DU KIT PARAMETRES (voir `Settings.tsx`) : champ et zone de
 * texte de 32 px, menu, menu d'ajout, choix d'icones, fichier, couleur. Tous prennent la
 * colonne du controle quand ils sont poses dans une `SettingsRow`.
 */

const FOCUS = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border';

/** Champ de saisie compact (32 px). Accepte une icone de recherche. */
export const SettingsInput = forwardRef<HTMLInputElement, InputProps>(function SettingsInput(
  { className, ...props },
  ref,
) {
  return <Input ref={ref} className={cn('h-8 w-full min-w-0 rounded-sm', className)} {...props} />;
});

export type SettingsTextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>;

/** Zone de texte : 32 px par defaut, plus haute avec `rows`. */
export const SettingsTextarea = forwardRef<HTMLTextAreaElement, SettingsTextareaProps>(
  function SettingsTextarea({ className, rows = 1, ...props }, ref) {
    return (
      <textarea
        ref={ref}
        rows={rows}
        className={cn(
          'w-full min-w-0 resize-none overflow-y-auto rounded-sm border border-border bg-surface px-3 py-1 text-sm text-ink-900 placeholder:text-ink-400',
          rows === 1 ? 'h-8' : 'min-h-8',
          FOCUS,
          className,
        )}
        {...props}
      />
    );
  },
);

/** Une entree de menu ; `inactif` = grisee, avec sa raison. */
export type SettingsMenuOption = OptionListEntry;

export interface SettingsMenuProps {
  /** Libelle accessible du menu. */
  label: string;
  /** Valeur choisie ; `''` ou absente de la liste = texte d'attente. */
  value: string;
  options: readonly SettingsMenuOption[];
  onChange: (value: string) => void;
  /** Texte quand rien n'est choisi. Defaut : « Choisir ». */
  placeholder?: string;
  disabled?: boolean;
}

/** Le menu de choix unique des parametres (remplace le `select` natif). */
export function SettingsMenu({
  label,
  value,
  options,
  onChange,
  placeholder = 'Choisir',
  disabled = false,
}: SettingsMenuProps): JSX.Element {
  const [ouvert, setOuvert] = useState(false);
  const bouton = useRef<HTMLButtonElement>(null);
  const choisie = options.find((option) => option.value === value);
  return (
    <div className="relative w-full min-w-0">
      <button
        ref={bouton}
        type="button"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={ouvert}
        disabled={disabled}
        onClick={() => setOuvert((etat) => !etat)}
        className={cn(
          'flex h-8 w-full min-w-0 items-center justify-between gap-2 rounded-sm border border-border bg-surface-1 px-4 text-left',
          'text-[0.8125rem] font-normal text-ink-900 transition-colors hover:border-border-strong',
          'disabled:cursor-not-allowed disabled:opacity-60',
          FOCUS,
        )}
      >
        <span className={cn('min-w-0 truncate', choisie === undefined && 'text-ink-400')}>
          {/* LE CHEMIN DE L'ENTREE CHOISIE, attenue comme dans la liste : une
              tache se lit « Projet X › Tâche Y ». */}
          {choisie?.prefixe !== undefined && choisie.prefixe !== '' && (
            <span className="text-ink-400">{`${choisie.prefixe} › `}</span>
          )}
          {choisie?.label ?? placeholder}
        </span>
        <IconChevronDown size={13} className="shrink-0 text-ink-400" />
      </button>
      {ouvert ? (
        <OptionList
          compact
          options={options}
          value={choisie === undefined ? null : value}
          recherche={options.length > SEUIL_RECHERCHE}
          onPick={(valeur) => {
            /* `OptionList` rend `null` pour la valeur vide : ici, une option comme une autre. */
            const choix = valeur ?? '';
            setOuvert(false);
            if (choix !== value) onChange(choix);
            bouton.current?.focus();
          }}
          onClose={() => setOuvert(false)}
        />
      ) : null}
    </div>
  );
}

/** Une entree du menu d'ajout. */
export interface SettingsAddOption {
  value: string;
  label: string;
  /** Un logo ou une icone de 20 px, a gauche du libelle. */
  icone?: ReactNode;
  /** Une seconde ligne, discrete. */
  detail?: string;
  /** Present : entree grisee, et cette raison ECRITE en seconde ligne (G16). */
  inactif?: string;
}

export interface SettingsAddMenuProps {
  /** Libelle du bouton, et du menu pour le lecteur d'ecran. Defaut : « Ajouter ». */
  label?: string;
  options: readonly SettingsAddOption[];
  /** Une entree active choisie : le menu se ferme, l'appelant fait le geste. */
  onChoose: (value: string) => void;
  /** Un ajout part : le bouton attend. */
  busy?: boolean;
}

/**
 * LE MENU D'AJOUT : un bouton secondaire « Ajouter » qui deroule les sortes de
 * choses qu'on peut ajouter (les fournisseurs d'un connecteur, avec leur logo),
 * pour quand UN clic sur une sorte suffit a lancer l'ajout. Meme vocabulaire
 * que les menus de gestes du socle (`TemplateMenu`, le « ... » de la fiche) :
 * `role="menu"`, `--shadow-pop`, `--radius-md`, entrees de 2 rem.
 *
 * UNE ENTREE GRISEE DIT POURQUOI, A L'ECRAN : sa raison est sa seconde ligne,
 * pas une infobulle (G16). Elle reste `aria-disabled` et focalisable, jamais
 * `disabled`, pour que le clavier la lise aussi.
 *
 * Clavier : fleches, Debut et Fin entre les entrees, Echap ferme et rend le
 * focus au bouton, Tab ferme ; un clic dehors ferme.
 */
export function SettingsAddMenu({ label = 'Ajouter', options, onChoose, busy = false }: SettingsAddMenuProps): JSX.Element {
  const [ouvert, setOuvert] = useState(false);
  const racine = useRef<HTMLDivElement>(null);
  const bouton = useRef<HTMLButtonElement>(null);
  const entrees = useRef<(HTMLButtonElement | null)[]>([]);
  const id = useId();

  useEffect(() => {
    if (!ouvert) return;
    entrees.current[0]?.focus();
    /* EN CAPTURE, comme `OptionList` : cliquer ailleurs ferme ce menu ET fait
       son geste, dans le meme clic. */
    const dehors = (event: PointerEvent): void => {
      if (racine.current != null && !racine.current.contains(event.target as Node)) setOuvert(false);
    };
    document.addEventListener('pointerdown', dehors, true);
    return () => document.removeEventListener('pointerdown', dehors, true);
  }, [ouvert]);

  const fermer = (): void => {
    setOuvert(false);
    bouton.current?.focus();
  };

  const auClavier = (event: KeyboardEvent<HTMLDivElement>): void => {
    const liste = entrees.current.filter((entree): entree is HTMLButtonElement => entree != null);
    const rang = liste.indexOf(document.activeElement as HTMLButtonElement);
    const aller = (index: number): void => {
      event.preventDefault();
      liste[(index + liste.length) % liste.length]?.focus();
    };
    if (event.key === 'Escape') {
      /* Echap ferme LE MENU, et rien d'autre (une fenetre autour reste ouverte). */
      event.stopPropagation();
      event.preventDefault();
      fermer();
    } else if (event.key === 'Tab') setOuvert(false);
    else if (event.key === 'ArrowDown') aller(rang + 1);
    else if (event.key === 'ArrowUp') aller(rang - 1);
    else if (event.key === 'Home') aller(0);
    else if (event.key === 'End') aller(liste.length - 1);
  };

  return (
    <div ref={racine} className="relative">
      <Button
        ref={bouton}
        variant="secondary"
        size="sm"
        aria-haspopup="menu"
        aria-expanded={ouvert}
        aria-controls={ouvert ? id : undefined}
        loading={busy}
        onClick={() => setOuvert((etat) => !etat)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !ouvert) {
            event.preventDefault();
            setOuvert(true);
          }
        }}
      >
        <IconPlus size={14} />
        {label}
      </Button>
      {ouvert ? (
        <div
          id={id}
          role="menu"
          aria-label={label}
          onKeyDown={auClavier}
          className={cn(
            'taki-anim-pop absolute right-0 top-[calc(100%+0.375rem)] z-30 flex min-w-60 max-w-80 flex-col p-1',
            'rounded-md border border-border bg-surface shadow-pop',
          )}
        >
          {options.map((option, index) => {
            const inactive = option.inactif !== undefined;
            const seconde = inactive ? option.inactif : option.detail;
            return (
              <button
                key={option.value}
                ref={(element) => { entrees.current[index] = element; }}
                type="button"
                role="menuitem"
                tabIndex={-1}
                aria-disabled={inactive || undefined}
                onClick={inactive ? undefined : () => { fermer(); onChoose(option.value); }}
                className={cn(
                  'flex min-h-8 w-full min-w-0 items-center gap-2.5 rounded-sm px-2 py-1.5 text-left font-sans text-[0.8125rem] transition-colors',
                  FOCUS,
                  inactive
                    ? 'cursor-default text-ink-300'
                    : 'cursor-pointer text-ink-700 hover:bg-surface-1 hover:text-ink-900 focus-visible:bg-surface-1',
                )}
              >
                {option.icone != null ? (
                  <span className={cn('flex h-5 w-5 shrink-0 items-center justify-center', inactive && 'opacity-50 grayscale')}>
                    {option.icone}
                  </span>
                ) : null}
                <span className="flex min-w-0 flex-col">
                  <span className="truncate font-medium">{option.label}</span>
                  {seconde !== undefined && seconde !== '' ? (
                    <span className={cn('truncate text-[0.6875rem]', inactive ? 'text-ink-400' : 'text-ink-500')}>{seconde}</span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export interface SettingsChoice {
  value: string;
  /** Le nom, au survol et pour le lecteur d'ecran. */
  label: string;
  /** Ce qu'on voit : une icone. */
  content: ReactNode;
}

export interface SettingsChoicesProps {
  label: string;
  options: readonly SettingsChoice[];
  value: string;
  onChange: (value: string) => void;
  /** Longue liste : hauteur bornee, defilement. */
  defile?: boolean;
}

/** Choix parmi des pastilles d'icones (une seule choisie). */
export function SettingsChoices({ label, options, value, onChange, defile = false }: SettingsChoicesProps): JSX.Element {
  const grille = (
    <div className="flex flex-wrap justify-end gap-1.5" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          title={option.label}
          aria-label={option.label}
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
          className={cn(
            'flex h-9 w-9 items-center justify-center rounded-md border transition-colors',
            FOCUS,
            option.value === value
              ? 'border-accent-border bg-accent-tint text-accent-ink'
              : 'border-border bg-surface text-ink-600 hover:bg-surface-1',
          )}
        >
          {option.content}
        </button>
      ))}
    </div>
  );
  if (!defile) return grille;
  return <div className="max-h-[15rem] w-full overflow-y-auto rounded-md border border-border-soft p-1.5">{grille}</div>;
}

export interface SettingsFileProps {
  /** Ce que le fichier remplace, pour les libelles accessibles (« du logo »). */
  label: string;
  /** Types acceptes (attribut `accept`). */
  accept: string;
  /** Formats, au survol du bouton. */
  formats?: string;
  /** Un fichier personnalise est en place : Rétablir est actif. */
  personnalise: boolean;
  busy?: boolean;
  disabled?: boolean;
  onFile: (file: File) => void;
  onDefault: () => void;
  /** L'apercu, a gauche dans la bande. */
  children: ReactNode;
}

/** Bande d'un fichier image : apercu, Choisir un fichier, Rétablir. */
export function SettingsFile({
  label,
  accept,
  formats,
  personnalise,
  busy = false,
  disabled = false,
  onFile,
  onDefault,
  children,
}: SettingsFileProps): JSX.Element {
  const fichier = useRef<HTMLInputElement>(null);
  return (
    <div className="flex w-full min-w-0 flex-wrap items-center gap-3 rounded-sm border border-border bg-surface-1 p-2">
      <div className="flex min-h-10 min-w-0 flex-1 items-center gap-3 overflow-hidden px-1">{children}</div>
      {disabled ? null : (
        <div className="ml-auto flex shrink-0 items-center gap-1">
          <input
            ref={fichier}
            type="file"
            accept={accept}
            className="hidden"
            aria-label={`Fichier ${label}`}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) onFile(file);
            }}
          />
          <Button variant="secondary" size="sm" disabled={busy} title={formats} onClick={() => fichier.current?.click()}>
            Choisir un fichier
          </Button>
          <Button variant="secondary" size="sm" disabled={!personnalise || busy} title="Rétablir par défaut" onClick={onDefault}>
            Rétablir
          </Button>
        </div>
      )}
    </div>
  );
}

export interface SettingsColorProps {
  label: string;
  /** La saisie en cours, telle que tapee. */
  value: string;
  onChange: (value: string) => void;
  /** Couleurs proposees, a droite. */
  palette: readonly { nom: string; hex: string }[];
  /** Repli du selecteur quand la saisie n'est pas une couleur. */
  defaut: string;
  disabled?: boolean;
}

/** Couleur : selecteur, saisie hexadecimale, palette. */
export function SettingsColor({ label, value, onChange, palette, defaut, disabled = false }: SettingsColorProps): JSX.Element {
  const hex = normaliserHex(value);
  const idErreur = `${label.replace(/\W+/g, '-').toLowerCase()}-erreur`;
  return (
    <div className="w-full min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <input
            type="color"
            aria-label={`Choisir : ${label}`}
            value={hex ?? defaut}
            disabled={disabled}
            onChange={(event) => onChange(event.target.value)}
            className="h-8 w-10 shrink-0 cursor-pointer rounded-sm border border-border bg-surface-1 p-1 disabled:cursor-not-allowed"
          />
          <SettingsInput
            aria-label={`${label} en hexadécimal`}
            aria-invalid={hex === null}
            aria-describedby={hex === null ? idErreur : undefined}
            value={value}
            maxLength={7}
            disabled={disabled}
            onChange={(event) => onChange(event.target.value)}
            className="w-24 font-mono text-[0.8125rem]"
          />
        </div>
        <div className="ml-auto flex flex-wrap gap-1" role="group" aria-label="Couleurs proposées">
          {palette.map((couleur) => (
            <button
              key={couleur.hex}
              type="button"
              title={couleur.nom}
              aria-label={couleur.nom}
              aria-pressed={hex === couleur.hex}
              disabled={disabled}
              onClick={() => onChange(couleur.hex)}
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-sm border disabled:cursor-not-allowed',
                FOCUS,
                hex === couleur.hex ? 'border-ink-700 bg-surface-3' : 'border-border hover:border-border-strong',
              )}
            >
              <span className="h-4 w-4 rounded-full" style={{ backgroundColor: couleur.hex }} />
            </button>
          ))}
        </div>
      </div>
      {hex === null ? (
        <p id={idErreur} role="alert" className="mt-1 text-xs text-danger">
          Couleur invalide
        </p>
      ) : null}
    </div>
  );
}
